// Dependencies
// -----------------------------------------------
import React, { useState } from 'react';
import styled from 'styled-components';

// Components
// -----------------------------------------------
import SimplifiedWysiwyg from '../inputs/simplified-wysiwyg';

// Theme tokens (guest "v2-modern" design system, brand-aware with fallbacks)
// -----------------------------------------------
const C = {
  primary: 'var(--primary-cta-color, #20c778)',
  primaryText: 'var(--primary-cta-text-color, #ffffff)',
  accent: 'var(--decorative-icon-color, var(--primary-cta-color, #20c778))',
  text: 'var(--base-color-text, #525860)',
  heading: 'var(--base-color-black, #040d18)',
  border: 'var(--base-color-border, #dbe1f4)',
  offWhite: 'var(--base-color-off-white, #f8f9f9)'
};

const RATING_LABELS = ['Terrible', 'Poor', 'Average', 'Great', 'Exceptional'];
const TITLE_MAX = 80;

// Styles
// -----------------------------------------------
const Card = styled.div`
  background: #fff;
  border: 1px solid ${C.border};
  border-radius: 20px;
  box-shadow: 0 24px 48px -24px rgba(4, 13, 24, 0.16);
  padding: 40px;
  box-sizing: border-box;

  @media (max-width: 600px) {
    padding: 24px 20px;
    border-radius: 16px;
  }
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${C.primary};
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 12px;
`;

const Title = styled.h1`
  color: ${C.heading};
  font-size: 30px;
  line-height: 1.15;
  font-weight: 700;
  margin: 0 0 10px;

  @media (max-width: 600px) {
    font-size: 25px;
  }
`;

const Intro = styled.p`
  color: ${C.text};
  font-size: 16px;
  line-height: 1.55;
  margin: 0;
`;

const Rule = styled.hr`
  border: none;
  border-top: 1px solid ${C.border};
  margin: 32px 0;
`;

const Field = styled.div`
  margin-bottom: 28px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FieldLabel = styled.label`
  display: block;
  color: ${C.heading};
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 2px;

  span {
    color: ${C.primary};
    margin-left: 2px;
  }
`;

const Hint = styled.p`
  color: ${C.text};
  font-size: 14px;
  line-height: 1.45;
  margin: 0 0 12px;
`;

const StarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StarButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  line-height: 0;
  transition: transform 0.12s ease;

  svg {
    display: block;
    width: 40px;
    height: 40px;
    fill: ${props => (props.$active ? C.primary : '#e4e6ef')};
    transition: fill 0.12s ease;
  }

  &:hover {
    transform: scale(1.08);
  }

  @media (max-width: 600px) {
    svg {
      width: 34px;
      height: 34px;
    }
  }
`;

const RatingWord = styled.div`
  min-height: 22px;
  margin-top: 8px;
  color: ${C.heading};
  font-size: 15px;
  font-weight: 700;
`;

const TitleInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  height: 52px;
  padding: 0 16px;
  font-size: 16px;
  font-family: inherit;
  color: ${C.heading};
  background: #fff;
  border: 1px solid ${C.border};
  border-radius: 12px;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: #a7acc3;
  }

  &:focus {
    border-color: ${C.primary};
    box-shadow: 0 0 0 3px color-mix(in srgb, ${C.primary} 22%, transparent);
  }
`;

const CharCount = styled.div`
  text-align: right;
  color: #a7acc3;
  font-size: 12px;
  margin-top: 6px;
`;

const EditorWrap = styled.div`
  .rdw-editor-toolbar {
    border-radius: 12px 12px 0 0 !important;
    border-color: ${C.border} !important;
  }

  .rdw-editor-main {
    border-radius: 0 0 12px 12px !important;
    border-color: ${C.border} !important;
    min-height: 180px !important;
    font-family: inherit !important;
  }

  figure {
    margin-bottom: 0;
  }
`;

const VerifiedNote = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 12px;
  background: ${C.offWhite};
  color: ${C.text};
  font-size: 14px;

  svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    fill: ${C.primary};
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
`;

const Disclaimer = styled.p`
  color: ${C.text};
  font-size: 13px;
  line-height: 1.4;
  margin: 0;
  flex: 1;
  min-width: 200px;
`;

const SubmitButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 52px;
  padding: 0 28px;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  color: ${C.primaryText};
  background: ${C.primary};
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: filter 0.15s ease, opacity 0.15s ease;

  svg {
    width: 16px;
    height: 16px;
    fill: currentColor;
  }

  &:hover:not(:disabled) {
    filter: brightness(0.94);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// Icons
// -----------------------------------------------
const StarIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 17.27l5.18 3.13-1.37-5.9 4.58-3.97-6.03-.52L12 4.5 9.64 10.01l-6.03.52 4.58 3.97-1.37 5.9z" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
  </svg>
);

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 11h12.17l-5.59-5.59L12 4l8 8-8 8-1.41-1.41L16.17 13H4z" />
  </svg>
);

// Star rating
// -----------------------------------------------
const StarRating = ({ rating, onRate }) => {
  const [hovered, setHovered] = useState(0);
  const shown = hovered || rating;

  return (
    <div>
      <StarRow onMouseLeave={() => setHovered(0)}>
        {[1, 2, 3, 4, 5].map(value => (
          <StarButton
            key={value}
            type="button"
            $active={shown >= value}
            aria-label={`${value} star${value > 1 ? 's' : ''}`}
            onClick={() => onRate(value)}
            onMouseEnter={() => setHovered(value)}
          >
            <StarIcon />
          </StarButton>
        ))}
      </StarRow>
      <RatingWord>{shown > 0 ? RATING_LABELS[shown - 1] : ''}</RatingWord>
    </div>
  );
};

// -----------------------------------------------
// COMPONENT->FORM -------------------------------
// -----------------------------------------------
const Form = props => {
  const title = props.title || '';

  return (
    <Card>
      <Eyebrow>Share your experience</Eyebrow>
      <Title>How was your stay?</Title>
      <Intro>
        Your review helps future guests book with confidence — and helps us keep
        delivering the getaway you deserve. It only takes a minute.
      </Intro>

      <Rule />

      <Field>
        <FieldLabel>
          Overall rating
          <span>*</span>
        </FieldLabel>
        <Hint>Tap a star to rate your stay.</Hint>
        <StarRating
          rating={props.rating}
          onRate={value => props.onChange('rating', value)}
        />
      </Field>

      <Field>
        <FieldLabel htmlFor="review-title">
          Review title
          <span>*</span>
        </FieldLabel>
        <Hint>Sum up your stay in a few words.</Hint>
        <TitleInput
          id="review-title"
          type="text"
          value={title}
          placeholder="e.g. Breathtaking oceanfront escape"
          onChange={e =>
            props.onChange('title', e.target.value.slice(0, TITLE_MAX))
          }
        />
        <CharCount>{`${title.length} / ${TITLE_MAX}`}</CharCount>
      </Field>

      <Field>
        <FieldLabel>
          Your review
          <span>*</span>
        </FieldLabel>
        <Hint>
          What did you love? What could be better? The details help other
          guests.
        </Hint>
        <EditorWrap>
          <SimplifiedWysiwyg
            name="body"
            placeholder="Write your review here..."
            onContentChange={props.onChange}
          />
        </EditorWrap>
      </Field>

      <Field>
        <VerifiedNote>
          <CheckIcon />
          Only verified guests who completed a stay can leave a review.
        </VerifiedNote>
      </Field>

      <Rule />

      <Actions>
        <Disclaimer>
          By submitting, you confirm this review reflects your genuine
          experience.
        </Disclaimer>
        <SubmitButton
          type="button"
          onClick={props.onSubmit}
          disabled={props.incomplete}
        >
          Submit review
          <ArrowIcon />
        </SubmitButton>
      </Actions>
    </Card>
  );
};

// Export
// -----------------------------------------------
export default Form;
